import { useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

// Nivel de corrección "Q": el QR se sigue leyendo con ~25 % dañado (pendones,
// folletos doblados o pantallas con reflejo)
const QR_LEVEL = 'Q';

/**
 * Link y QR de una feria (PLAN_FERIAS). La vista previa es SVG; se descarga en
 * PNG de 1024 px (pantallas, folletos) o SVG vectorial (impresión grande). El
 * link no cambia aunque cambien el nombre o las vacantes de la feria.
 *
 * Props: url (link público), slug (para el nombre del archivo), previewSize.
 */
const AtsFairQr = ({ url, slug, previewSize = 180 }) => {
  const { t } = useTranslation();
  const svgRef = useRef(null);
  const canvasRef = useRef(null);
  const fileBase = `qr-feria-${slug || 'feria'}`;

  const triggerDownload = (href, filename) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPng = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    triggerDownload(canvas.toDataURL('image/png'), `${fileBase}.png`);
  };

  const handleDownloadSvg = () => {
    const svg = svgRef.current?.querySelector('svg');
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${xml}`], { type: 'image/svg+xml' });
    const href = URL.createObjectURL(blob);
    triggerDownload(href, `${fileBase}.svg`);
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  };

  const handleCopy = async () => {
    const { showAlert } = useStore.getState();
    try {
      await navigator.clipboard.writeText(url);
      showAlert(t('ats.fairs.linkCopied'), 'success');
    } catch (err) {
      console.error('Error copiando el link de la feria:', err);
      showAlert(t('ats.fairs.linkCopyError'), 'error');
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
      <Box
        ref={svgRef}
        data-testid="fair-qr"
        sx={{ p: 1, backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: 2, lineHeight: 0 }}
      >
        <QRCodeSVG value={url} size={previewSize} level={QR_LEVEL} marginSize={2} />
      </Box>
      {/* Canvas en alta resolución, oculto: solo para descargar el PNG */}
      <Box ref={canvasRef} sx={{ display: 'none' }} aria-hidden>
        <QRCodeCanvas value={url} size={1024} level={QR_LEVEL} marginSize={4} />
      </Box>
      <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
        <Typography variant="caption" color="text.secondary">
          {t('ats.fairs.editor.linkLabel')}
        </Typography>
        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all', mb: 1.5 }} data-testid="fair-url">
          {url}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" startIcon={<CopyIcon fontSize="small" />} onClick={handleCopy}>
            {t('ats.fairs.copyLink')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenIcon fontSize="small" />}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('ats.fairs.openPage')}
          </Button>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon fontSize="small" />} onClick={handleDownloadPng}>
            {t('ats.fairs.editor.downloadPng')}
          </Button>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon fontSize="small" />} onClick={handleDownloadSvg}>
            {t('ats.fairs.editor.downloadSvg')}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('ats.fairs.editor.linkHint')}
        </Typography>
      </Box>
    </Box>
  );
};

export default AtsFairQr;
